import { apiGet, apiPatch, apiPost } from './client.js';

export interface PartsRequest {
  id: string;
  ticketId: string;
  ticketRepairId?: string | null;
  ticketNumber?: string;
  customerName?: string;
  partName: string;
  supplierId?: string | null;
  supplierName?: string | null;
  quantityNeeded: number;
  urgency: string;
  status: string;
  supplierNotes?: string | null;
  poNumber?: string | null;
  createdAt: string;
  updatedAt: string;
}

export function createPartsRequest(body: Record<string, unknown>) {
  return apiPost<PartsRequest, Record<string, unknown>>('/api/v1/parts-requests', body).then((res) => res.data);
}

export function listPartsRequests(params?: { status?: string; ticketId?: string }) {
  return apiGet<PartsRequest[]>('/api/v1/parts-requests', { params }).then((res) => res.data);
}

export function updatePartsRequest(id: string, body: { status: string; reason?: string }) {
  return apiPatch<PartsRequest, typeof body>(`/api/v1/parts-requests/${id}`, body).then((res) => res.data);
}

export type PartsRequestQueueGroup = {
  key: string;
  partName: string;
  sku: string | null;
  inventoryItemId: string | null;
  qtyOnHand: number;
  qtyOnOrder: number;
  need: number;
  preferredSupplierId: string | null;
  preferredSupplierName: string | null;
  preferredCost: string | null;
  leadTimeDays: number | null;
  requests: Array<{
    id: string;
    ticketId: string;
    ticketNumber: string;
    customerName: string;
    quantityNeeded: number;
    status: string;
    createdAt: string;
    ageHours: number;
    promisedDate: string | null;
    requestedBy: string | null;
    poNumber: string | null;
    partName: string;
  }>;
};

export type PartsRequestFlat = PartsRequestQueueGroup['requests'][number] & {
  partName: string;
  supplierName: string | null;
  preferredSupplierName: string | null;
};

export function getPartsRequestQueue(params: Record<string, string | number | undefined>) {
  return apiGet<{
    view: string;
    openCount: number;
    groups: PartsRequestQueueGroup[];
    requests: PartsRequestFlat[];
  }>('/api/v1/parts-requests/queue', { params }).then((res) => res.data);
}

export function getOpenPartsRequestCount() {
  return apiGet<{ openCount: number }>('/api/v1/parts-requests/open-count').then((res) => res.data);
}

export function createPurchaseOrdersFromRequests(body: {
  requestIds: string[];
  lineQuantities: Array<{ inventoryItemId: string; qty: number }>;
}) {
  return apiPost<
    {
      purchaseOrders: Array<{ id: string; poNumber: string; supplierName: string; lineCount: number }>;
      skipped: Array<{ requestId: string; reason: string }>;
    },
    typeof body
  >('/api/v1/parts-requests/purchase-orders', body).then((res) => res.data);
}

export function allocatePartsRequests(requestIds: string[]) {
  return apiPost<
    { fulfilled: Array<{ ticketNumber: string; partName: string }>; leftOrdered: number },
    { requestIds: string[] }
  >('/api/v1/parts-requests/allocate', { requestIds }).then((res) => res.data);
}
